import * as React from 'react';
import { useState, useMemo } from 'react';

import styled from '@emotion/styled';
import { DateTime } from 'luxon';

import { Box, Button, Text, COLORS } from '@clutter/clean';

import { currency } from '@shared/utils/currency';

import { StorageTreasuresAuctionFragment } from '@portal/schema';

import { IStepProps } from '../form';
import { Base } from './base';

const Header = styled(Text.Title)`
  margin-bottom: 32px;
`;

const Subheader = styled(Text.Body)`
  margin-bottom: 24px;
  color: ${COLORS.hippo};
`;

const WarehouseGroup = styled.div`
  margin-bottom: 32px;
`;

const WarehouseHeader = styled.div`
  background-color: ${COLORS.grayBackground};
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 8px;
`;

const WarehouseAddress = styled(Text.Body)`
  font-weight: 600;
`;

const CheckboxCell = styled.td`
  width: 40px;
  vertical-align: middle;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const SelectableRow = styled.tr<{ selected: boolean; disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background-color: ${({ selected }) => (selected ? COLORS.grayBackground : 'transparent')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${({ disabled }) => (disabled ? 'transparent' : COLORS.grayBackground)};
  }
`;

const PriceCell = styled.td`
  text-align: right;
  font-weight: 500;
`;

const TotalSection = styled.div`
  background-color: ${COLORS.grayBackground};
  padding: 16px;
  border-radius: 4px;
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FROM_ISO_OPTIONS = { setZone: true };

interface WarehouseAuctionGroup {
  warehouseId: string;
  warehouseAddress: string;
  auctions: StorageTreasuresAuctionFragment[];
}

const formatAddress = (address: { street?: string | null; city?: string | null; state?: string | null }): string => {
  const parts = [address.street, address.city, address.state].filter(Boolean);
  return parts.join(', ') || 'Unknown Location';
};

const getAuctionFee = (auction: StorageTreasuresAuctionFragment): number => {
  return auction.winnerInvoice?.clutterAmount ?? 0;
};

export const StepAuctionSelection: React.FC<
  {
    options: StorageTreasuresAuctionFragment[];
    onSelect(selectedAuctions: StorageTreasuresAuctionFragment[]): void;
  } & IStepProps
> = ({ options, onSelect }) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | null>(null);

  // Group auctions by warehouse
  const warehouseGroups = useMemo((): WarehouseAuctionGroup[] => {
    const groups = new Map<string, WarehouseAuctionGroup>();

    options.forEach((auction) => {
      const warehouseId = auction.warehouse.id;
      const warehouseAddress = formatAddress(auction.warehouse.address);

      if (!groups.has(warehouseId)) {
        groups.set(warehouseId, {
          warehouseId,
          warehouseAddress,
          auctions: [],
        });
      }
      groups.get(warehouseId)!.auctions.push(auction);
    });

    // Sort groups by warehouse address
    return Array.from(groups.values()).sort((a, b) => a.warehouseAddress.localeCompare(b.warehouseAddress));
  }, [options]);

  const toggleSelection = (auction: StorageTreasuresAuctionFragment) => {
    const warehouseId = auction.warehouse.id;

    // If selecting an auction from a different warehouse, clear previous selections
    if (selectedWarehouseId && selectedWarehouseId !== warehouseId) {
      setSelectedIds(new Set([auction.id]));
      setSelectedWarehouseId(warehouseId);
      return;
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(auction.id)) {
        next.delete(auction.id);
        // If no more selections, clear the warehouse lock
        if (next.size === 0) {
          setSelectedWarehouseId(null);
        }
      } else {
        next.add(auction.id);
        setSelectedWarehouseId(warehouseId);
      }
      return next;
    });
  };

  const selectAllInWarehouse = (warehouseId: string, auctions: StorageTreasuresAuctionFragment[]) => {
    const auctionIds = auctions.map((a) => a.id);
    const allSelected = auctionIds.every((id) => selectedIds.has(id));

    if (allSelected) {
      // Deselect all in this warehouse
      setSelectedIds((prev) => {
        const next = new Set(prev);
        auctionIds.forEach((id) => next.delete(id));
        if (next.size === 0) {
          setSelectedWarehouseId(null);
        }
        return next;
      });
    } else {
      // Select all in this warehouse (clearing other warehouse selections)
      setSelectedIds(new Set(auctionIds));
      setSelectedWarehouseId(warehouseId);
    }
  };

  const handleContinue = () => {
    const selectedAuctions = options.filter((auction) => selectedIds.has(auction.id));
    onSelect(selectedAuctions);
  };

  const someSelected = selectedIds.size > 0;
  const totalFee = options
    .filter((auction) => selectedIds.has(auction.id))
    .reduce((sum, auction) => sum + getAuctionFee(auction), 0);

  const hasMultipleWarehouses = warehouseGroups.length > 1;

  return (
    <Base>
      <Header size="large">Unscheduled Auction Pickups</Header>
      <Subheader>
        Select the auctions you would like to schedule for pickup together.
        {hasMultipleWarehouses && (
          <>
            <br />
            <br />
            <strong>Note:</strong> Auctions at different facilities must be scheduled separately.
          </>
        )}
      </Subheader>

      {warehouseGroups.map((group) => {
        const isDisabled = selectedWarehouseId !== null && selectedWarehouseId !== group.warehouseId;
        const allInGroupSelected = group.auctions.every((a) => selectedIds.has(a.id));

        return (
          <WarehouseGroup key={group.warehouseId}>
            {hasMultipleWarehouses && (
              <WarehouseHeader>
                <Box.Flex justifyContent="space-between" alignItems="center">
                  <WarehouseAddress>{group.warehouseAddress}</WarehouseAddress>
                  {!isDisabled && (
                    <Button
                      kind="secondary"
                      size="small"
                      onClick={() => selectAllInWarehouse(group.warehouseId, group.auctions)}
                    >
                      {allInGroupSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                </Box.Flex>
              </WarehouseHeader>
            )}
            {!hasMultipleWarehouses && (
              <Box.Flex justifyContent="flex-end" margin="0 0 16px 0">
                <Button
                  kind="secondary"
                  size="small"
                  onClick={() => selectAllInWarehouse(group.warehouseId, group.auctions)}
                >
                  {allInGroupSelected ? 'Deselect All' : 'Select All'}
                </Button>
              </Box.Flex>
            )}
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }} />
                  <th>
                    <Text>Auction ID</Text>
                  </th>
                  <th>
                    <Text>Auction Close Date</Text>
                  </th>
                  <th style={{ textAlign: 'right' }}>
                    <Text>Pickup Fee</Text>
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {group.auctions.map((auction) => {
                  const isSelected = selectedIds.has(auction.id);
                  const fee = getAuctionFee(auction);
                  return (
                    <SelectableRow
                      key={auction.id}
                      selected={isSelected}
                      disabled={isDisabled}
                      onClick={() => !isDisabled && toggleSelection(auction)}
                    >
                      <CheckboxCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => !isDisabled && toggleSelection(auction)}
                          aria-label={`Select auction ${auction.storageTreasuresID} from ${DateTime.fromISO(auction.endTime, FROM_ISO_OPTIONS).toLocaleString(DateTime.DATE_HUGE)}`}
                        />
                      </CheckboxCell>
                      <td>{auction.storageTreasuresID ?? '-'}</td>
                      <td>{DateTime.fromISO(auction.endTime, FROM_ISO_OPTIONS).toLocaleString(DateTime.DATE_HUGE)}</td>
                      <PriceCell>{fee > 0 ? currency(fee) : '-'}</PriceCell>
                      <td align="right">
                        {auction.storageTreasuresLink && (
                          <a
                            href={auction.storageTreasuresLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View on Storage Treasures
                          </a>
                        )}
                      </td>
                    </SelectableRow>
                  );
                })}
              </tbody>
            </table>
          </WarehouseGroup>
        );
      })}

      {someSelected && (
        <TotalSection>
          <Text.Body>
            <strong>
              {selectedIds.size} auction{selectedIds.size > 1 ? 's' : ''} selected
            </strong>
          </Text.Body>
          <Text.Body>
            <strong>Total Pickup Fee: {currency(totalFee)}</strong>
          </Text.Body>
        </TotalSection>
      )}

      <Box.Flex justifyContent="flex-end" margin="24px 0 0 0">
        <Button onClick={handleContinue} disabled={!someSelected}>
          Schedule Pickup{selectedIds.size > 1 ? ` (${selectedIds.size} auctions)` : ''}
        </Button>
      </Box.Flex>
    </Base>
  );
};
